import DOMPurify from "dompurify";
import { marked } from "marked";
import { formatTermsToHtml } from "./formatTermsHtml";

marked.setOptions({
  gfm: true,
  breaks: true,
});

const PURIFY_CONFIG: DOMPurify.Config = {
  ALLOWED_TAGS: [
    "h1",
    "h2",
    "h3",
    "h4",
    "h5",
    "h6",
    "p",
    "br",
    "hr",
    "ul",
    "ol",
    "li",
    "strong",
    "em",
    "b",
    "i",
    "u",
    "s",
    "sub",
    "sup",
    "blockquote",
    "code",
    "pre",
    "table",
    "thead",
    "tbody",
    "tr",
    "th",
    "td",
    "a",
    "div",
    "span",
  ],
  ALLOWED_ATTR: ["href", "target", "rel", "class", "style", "data-list"],
};

function normalizeRenderedHtml(html: string): string {
  if (typeof window === "undefined") return html;

  const root = window.document.createElement("div");
  root.innerHTML = html;

  root.querySelectorAll(".ql-ui").forEach((node) => node.remove());

  root.querySelectorAll("ol").forEach((list) => {
    const items = Array.from(list.children).filter(
      (child): child is HTMLLIElement => child instanceof HTMLLIElement
    );

    if (!items.length) return;

    const allBulletItems = items.every(
      (item) => item.getAttribute("data-list") === "bullet"
    );

    items.forEach((item) => item.removeAttribute("data-list"));

    if (!allBulletItems) return;

    const ul = window.document.createElement("ul");
    ul.className = list.className;
    items.forEach((item) => ul.appendChild(item));
    list.replaceWith(ul);
  });

  return root.innerHTML;
}

function looksLikeRawHtmlBlock(s: string): boolean {
  const t = s.trim();
  return /^<[a-z!?]/i.test(t);
}

/** Legacy numbered plain text (e.g. from public/terms-and-conditions.txt) without Markdown # headers. */
function isStructuredPlainText(s: string): boolean {
  if (/^#{1,6}\s/m.test(s)) return false;
  if (looksLikeRawHtmlBlock(s)) return false;
  return (
    /(?:^|\n)\s*\d+\.\d+\s+\S/.test(s) ||
    /(?:^|\n)\s*\d+\.\s+[A-Za-z0-9"“]/.test(s)
  );
}

/**
 * Renders stored terms for the public page: HTML (sanitized), legacy plain (structured),
 * or Markdown (from qfo-admin MDEditor). Output is safe for `dangerouslySetInnerHTML`.
 */
export function buildTermsDocumentHtml(raw: string): string {
  const t = raw.replace(/\r\n/g, "\n").trim();
  if (!t) return "";

  if (looksLikeRawHtmlBlock(t)) {
    return normalizeRenderedHtml(DOMPurify.sanitize(t, PURIFY_CONFIG));
  }

  if (isStructuredPlainText(t)) {
    return DOMPurify.sanitize(formatTermsToHtml(t), PURIFY_CONFIG);
  }

  const mdHtml = marked(t, { async: false }) as string;
  return DOMPurify.sanitize(mdHtml, PURIFY_CONFIG);
}